import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SEOMetadata from '@/components/SEOMetadata';
import { ArrowRight, Globe, Shield, Zap, TrendingUp, Calendar, Clock } from 'lucide-react';

const NewsHomepage = () => {
  const featuredArticles = [
    {
      id: 1,
      title: "The Future of Data Center Infrastructure in 2024",
      excerpt: "Exploring emerging trends in data center technology, sustainability, and edge computing solutions.",
      category: "Infrastructure",
      readTime: "5 min read",
      date: "2024-01-15",
      slug: "future-data-center-infrastructure-2024",
      featured: true
    },
    {
      id: 2,
      title: "Residential Proxy Networks: Security and Compliance Best Practices",
      excerpt: "Understanding the legal and technical requirements for residential proxy deployments.",
      category: "Security",
      readTime: "7 min read",
      date: "2024-01-12",
      slug: "residential-proxy-security-compliance"
    },
    {
      id: 3,
      title: "Edge Computing: Reducing Latency for Global Applications",
      excerpt: "How edge computing infrastructure is transforming application performance worldwide.",
      category: "Technology",
      readTime: "6 min read",
      date: "2024-01-10",
      slug: "edge-computing-latency-reduction"
    },
    {
      id: 4,
      title: "VPS vs Dedicated Servers: Making the Right Choice",
      excerpt: "Comprehensive comparison of virtualization technologies and dedicated hardware solutions.",
      category: "Infrastructure",
      readTime: "8 min read",
      date: "2024-01-08",
      slug: "vps-vs-dedicated-servers-comparison"
    }
  ];

  const categories = [
    { name: "Infrastructure", icon: Globe, count: 12, color: "bg-blue-500" },
    { name: "Security", icon: Shield, count: 8, color: "bg-green-500" },
    { name: "Technology", icon: Zap, count: 15, color: "bg-purple-500" },
    { name: "Industry Insights", icon: TrendingUp, count: 6, color: "bg-orange-500" }
  ];

  const featuredArticle = featuredArticles.find(article => article.featured);
  const regularArticles = featuredArticles.filter(article => !article.featured);

  return (
    <div className="min-h-screen bg-background">
      <SEOMetadata
        title="NEXX News & Insights - Infrastructure, Technology & Industry Updates"
        description="Stay updated with the latest in data center infrastructure, proxy networks, VPS technology, and industry insights from NEXX. Expert analysis and technical deep-dives."
        keywords="data center news, infrastructure updates, proxy technology, VPS insights, edge computing, network security, technology trends"
        canonical="https://news.nexx.net"
        ogImage="https://nexx.net/og-news.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "NewsMediaOrganization",
          "name": "NEXX News & Insights",
          "url": "https://news.nexx.net",
          "description": "Technology news and insights from NEXX infrastructure experts",
          "publisher": {
            "@type": "Organization",
            "name": "NEXX",
            "url": "https://nexx.net"
          }
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link to="https://nexx.net" className="flex items-center space-x-3">
                <img 
                  src="/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png" 
                  alt="NEXX"
                  className="h-8 w-auto"
                />
                <span className="font-semibold text-lg text-foreground">News</span>
              </Link>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="https://nexx.net">
                <Button variant="outline" size="sm">
                  Main Site
                </Button>
              </Link>
              <Link to="https://nexx.net/consultation">
                <Button size="sm">
                  Get Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              NEXX News & Insights
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Stay ahead with expert analysis on data center infrastructure, proxy networks, 
              VPS technology, and the latest industry trends shaping the digital landscape.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => {
                const IconComponent = category.icon;
                return (
                  <Badge key={category.name} variant="secondary" className="px-4 py-2">
                    <IconComponent className="w-4 h-4 mr-2" />
                    {category.name} ({category.count})
                  </Badge>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">Featured Article</h2>
            <Card className="max-w-4xl mx-auto overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/3 bg-gradient-to-br from-primary/10 to-secondary/10 p-8 flex items-center justify-center">
                  <div className="text-center">
                    <TrendingUp className="w-16 h-16 mx-auto mb-4 text-primary" />
                    <Badge className="mb-2">{featuredArticle.category}</Badge>
                  </div>
                </div>
                <div className="md:w-2/3 p-8">
                  <CardHeader className="p-0 mb-4">
                    <CardTitle className="text-2xl mb-2">{featuredArticle.title}</CardTitle>
                    <CardDescription className="text-base">{featuredArticle.excerpt}</CardDescription>
                  </CardHeader>
                  <div className="flex items-center text-sm text-muted-foreground mb-6">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(featuredArticle.date).toLocaleDateString()}
                    <Clock className="w-4 h-4 ml-4 mr-2" />
                    {featuredArticle.readTime}
                  </div>
                  <Link to={`/${featuredArticle.slug}`}>
                    <Button className="w-full md:w-auto">
                      Read Article
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* Recent Articles */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">Latest Insights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {regularArticles.map((article) => (
              <Card key={article.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">{article.category}</Badge>
                    <span className="text-sm text-muted-foreground">{article.readTime}</span>
                  </div>
                  <CardTitle className="text-lg">{article.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{article.excerpt}</CardDescription>
                </CardContent>
                <CardFooter className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {new Date(article.date).toLocaleDateString()}
                  </span>
                  <Link to={`/${article.slug}`}>
                    <Button variant="ghost" size="sm">
                      Read More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Overview */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">Explore Topics</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Card key={category.name} className="text-center hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <div className={`w-12 h-12 ${category.color} rounded-lg mx-auto mb-4 flex items-center justify-center`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-lg">{category.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{category.count} articles</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Infrastructure?</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Connect with our infrastructure experts to discuss your specific needs and discover 
            how NEXX can optimize your business operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="https://nexx.net/consultation">
              <Button size="lg">
                Schedule Consultation
              </Button>
            </Link>
            <Link to="https://nexx.net">
              <Button variant="outline" size="lg">
                Explore Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-card/50 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xs">N</span>
              </div>
              <span className="text-sm text-muted-foreground">
                © 2024 NEXX. All rights reserved.
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="https://nexx.net/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
              <Link to="https://nexx.net/general-tos" className="text-sm text-muted-foreground hover:text-foreground">
                Terms of Service
              </Link>
              <Link to="https://nexx.net" className="text-sm text-muted-foreground hover:text-foreground">
                Main Site
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewsHomepage;